import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { CrtTextTruncatedComponent } from 'src/app/crt/components/text-truncated/text-truncated.component';
import { deepCopy } from '../../../../shared/utils/deepCopy';

export type FilterSelectState = 'all' | 'semi' | 'none';

export interface ObjectFilterResult {
  filterValues: ObjectFilterModel[];
  isFiltered: boolean;
}

export interface ObjectFilterModel {
  checked: boolean;
  label: string;
  selectState?: FilterSelectState;
  subValues?: ObjectFilterSubModel[];
  prop: string;
}

export interface ObjectFilterSubModel {
  checked: boolean;
  label: string;
  prop: string;
  parentLabel?: string;
  selectState?: FilterSelectState;
  subValues?: ObjectFilterSubModel[];
}

@Component({
  selector: 'object-filter',
  templateUrl: 'object-filter.html',
  styleUrl: 'object-filter.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonicModule, CommonModule, FormsModule, CrtTextTruncatedComponent],
})
export class ObjectFilterPage implements OnInit {
  private readonly modalCtrl = inject(ModalController);
  protected deepFilterValues = signal<ObjectFilterModel[]>([]);
  protected displaySubs = computed(() => this.deepFilterValues().some((v) => v.subValues?.length > 0));

  protected isAllSelected = signal(true);
  @Input()
  public filterValues = [];
  @Input()
  public title: string;
  @Input()
  public subtitle: string;
  @Input()
  public textButton: string = $localize`Sélectionner`;

  ngOnInit(): void {
    this.deepFilterValues.set(
      deepCopy<ObjectFilterModel[]>(this.filterValues).map((values) => {
        if (values.subValues) {
          values.selectState = this.checkSelectState(values);
          return {
            ...values,
            subValues: this.mapWithParentLabel(values.subValues, values.label),
          };
        }
        this.sortNode(values);
        return values;
      })
    );
    this.refreshAllSelected();
  }

  private refreshAllSelected() {
    this.isAllSelected.set(
      this.deepFilterValues().every((value) => {
        const isSubValuesChecked = value.subValues
          ? value.subValues.every((subValue) => subValue.selectState === 'all')
          : true;
        return value.checked && isSubValuesChecked;
      })
    );
  }

  private mapWithParentLabel(subs: ObjectFilterSubModel[], label: string): ObjectFilterSubModel[] {
    return subs?.map((sub) => ({
      ...sub,
      parentLabel: label,
      subValues: this.mapWithParentLabel(sub.subValues, sub.label),
    }));
  }

  protected selectAll(event: CustomEvent) {
    this.deepFilterValues.update((values) => {
      values.forEach((filterValue) => {
        filterValue.checked = event.detail.checked;
        this.selectChild(event.detail.checked, filterValue.subValues);
        filterValue.selectState = this.checkSelectState(filterValue);
      });
      values.forEach((v) => this.sortNode(v));
      return values;
    });
  }

  private selectChild(check: boolean, childs: ObjectFilterSubModel[]) {
    childs?.forEach((sub) => {
      sub.checked = check;
      this.selectChild(check, sub.subValues);
    });
  }

  protected onFilterChanged(
    filter: ObjectFilterModel & {
      parentLabel?: string;
    },
    event: CustomEvent
  ) {
    this.deepFilterValues.update((values) => {
      values.map((v) => {
        if (v.label === filter.label) {
          v.checked = event.detail.checked;
          this.selectChild(event.detail.checked, v.subValues);
        } else {
          this.onSubChanged(event.detail.checked, v.subValues, filter.label, filter.parentLabel);
        }
        v.selectState = this.checkSelectState(v);
        return v;
      });
      values.forEach((v) => this.sortNode(v));
      return values;
    });
    this.refreshAllSelected();
  }

  private checkSelectState(filter: ObjectFilterModel): FilterSelectState {
    if (filter.subValues && filter.prop !== 'descriptionMetier') {
      const subStates = filter.subValues.map((subValue) => this.checkSelectState(subValue));

      if (filter.subValues.every((s, index) => s.checked && subStates[index] === 'all')) {
        filter.checked = true;
        filter.selectState = 'all';
        return 'all';
      } else if (filter.subValues.every((s, index) => !s.checked && subStates[index] === 'none')) {
        filter.checked = false;
        filter.selectState = 'none';
        return 'none';
      } else {
        filter.selectState = 'semi';
        return 'semi';
      }
    }

    filter.selectState = filter.checked ? 'all' : 'none';
    return filter.selectState;
  }

  private onSubChanged(check: boolean, childs: ObjectFilterSubModel[], label: string, parentLabel: string) {
    childs?.forEach((child) => {
      if (child.parentLabel === parentLabel && child.label === label) {
        child.checked = check;
        this.selectChild(check, child.subValues);
      } else {
        this.onSubChanged(check, child.subValues, label, parentLabel);
      }
    });
  }

  protected closeModal() {
    this.modalCtrl.dismiss();
  }

  private sortNode(node: ObjectFilterSubModel) {
    if (node.subValues) {
      node.subValues.sort((a, b) => (a.label > b.label ? 1 : -1));
      node.subValues.forEach((sub) => {
        this.sortNode(sub);
      });
    }
  }

  protected applyFilter() {
    this.modalCtrl.dismiss({
      filterValues: this.deepFilterValues(),
      isFiltered: !this.isAllSelected(),
    });
  }
}
